// pdfGenerator.js
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import logo from "../Images/paras&roadlines.jpg";
import logo1 from "../Images/para.png";
import { getStateCode } from "./states";

pdfMake.vfs = pdfFonts.vfs;

const DUMMY_DATA = {
  lr_number: "",
  date: "",
  consignor_name: "",
  consignee_bank_name: "",
  from_location: "",
  to_location: "",
  demurrage_days: "",
  demurrage_rate: "",
  insurance_company: "",
  policy_no: "",
  insurance_date: "",
  insurance_amount: "",
  insurance_risk: "",
  delivery_office_address: "",
  state: "",
  tel_no: "",
  issuing_office_address: "POWER HOUSE BHILAI (C.G.)",
  vehicle_no: "",
  eway_bill_no: "",
  classification_of_goods: "",
  method_of_packing: "",
  invoice_no: "",
  loading_in_date: "",
  loading_out_date: "",
  unloading_in_date: "",
  unloading_out_date: "",
  bill_to: "",
  package_items: [
    {
      packages: "",
      description: "",
      actual_weight: "",
      charged_weight: "",
      rate: "",
      amount: "",
    },
  ],
  freight: "",
  mazdoor_charges: "",
  service_tax_cess: "",
  sur_ch: "",
  st_ch: "",
  risk_ch: "",
  total_rs: "",
  total_p: "",
  billed_at: "BHILAI",
  value: "",
};

const fmtDate = (v) => {
  if (!v) return "";
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return v;
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const truncate = (str, maxLen) => {
  if (!str) return "";
  const s = String(str);
  return s.length > maxLen ? s.slice(0, maxLen - 1).trim() + "…" : s;
};

/* ─────────────────────────────────────────────────────────
   REUSABLE LAYOUT / TABLE HELPERS
   (replaces the many hand-written duplicate layout objects)
   ───────────────────────────────────────────────────────── */

// Generic border-layout factory. Pass overrides only where needed.
const makeLayout = ({
  hWidth = 0.5,
  vWidth = 0.5,
  color = "#000000",
  pad = [2, 2, 1, 1], // [left, right, top, bottom]
  hRule = null, // optional custom (i, node) => number fn
  vRule = null,
} = {}) => ({
  hLineWidth: hRule || (() => hWidth),
  vLineWidth: vRule || (() => vWidth),
  hLineColor: () => color,
  vLineColor: () => color,
  paddingLeft: () => pad[0],
  paddingRight: () => pad[1],
  paddingTop: () => pad[2],
  paddingBottom: () => pad[3],
});

const fullBorder = makeLayout({
  hWidth: 0.75,
  vWidth: 0.75,
  pad: [3, 3, 1, 1],
});
const redBorder = makeLayout({
  hWidth: 1.25,
  vWidth: 1.25,
  color: "red",
  pad: [4, 4, 1, 1],
});
const thinBorder = makeLayout({ pad: [2, 2, 1, 1] });
const noBorder = makeLayout({ hWidth: 0, vWidth: 0, pad: [0, 0, 0, 0] });

// Wrap a single-column stack of row-defs into a bordered table.
// rows: array of pdfmake cell-content objects (one per row)
const singleColTable = (rows, layout = thinBorder, margin) => ({
  table: { widths: ["*"], body: rows.map((r) => [r]) },
  layout,
  ...(margin ? { margin } : {}),
});

// Simple bordered box built from plain text lines (each its own row)
const textBox = (lines, layout = thinBorder, margin) =>
  singleColTable(
    lines.map((l) => (typeof l === "string" ? { text: l } : l)),
    layout,
    margin,
  );

/* ───────────────────────────────────────────────────────── */

export const generateLorryReceiptPDF = (rowDataInput) => {
  const raw = rowDataInput || {};

  // ── bill_to mapping ──
  let bill_to = "";
  if (raw.is_consignor) bill_to = "consignor";
  else if (raw.is_consignee) bill_to = "consignee";
  else if (raw.is_transporter || raw.truck_owner) bill_to = "transporter";

  // ── package_items fallback ──
  const package_items =
    Array.isArray(raw.package_items) && raw.package_items.length > 0
      ? raw.package_items
      : DUMMY_DATA.package_items;

  // ── mapped data object ──
  const d = {
    ...DUMMY_DATA,

    // LR & Date
    lr_number: raw.lr_number || "",
    date: raw.loading_date || raw.created_at || "",

    // Consignor
    consignor_name: raw.consignor_name || raw.party_name || "",
    consignor_address: raw.consignor_address || raw.party_address || "",

    truck_owner: raw.truck_owner || "",

    // Consignee
    consignee_bank_name:
      raw.consignee_bank_name ||
      raw.consignee_name ||
      raw.consignee_party_name ||
      "",
    consignee_bank_address:
      raw.consignee_bank_address ||
      raw.consignee_address ||
      raw.consignee_party_address ||
      "",
    // Locations
    from_location: raw.from_location || "",
    to_location: raw.to_location || "",

    // Demurrage
    demurrage_days: raw.demurrage_days || "",
    demurrage_rate: raw.demurrage_rate || "",

    // Insurance (not in API — keep empty)
    insurance_company: "",
    policy_no: "",
    insurance_date: "",
    insurance_amount: "",
    insurance_risk: "",

    // Delivery / State / Tel
    delivery_office_address: raw.delivery_address || "",
    state: raw.state || "",
    tel_no: raw.tel_no || "",

    // Issuing office & Vehicle
    issuing_office_address: "POWER HOUSE BHILAI (C.G.)",
    vehicle_no: raw.truck_no || "",

    eway_bill_no: raw.eway_bill_no || "",
    eway_bill_date: raw.eway_bill_date || "",
    eway_validity_date: raw.eway_validity_date || "",
    // Goods info
    classification_of_goods: raw.classification_of_goods || "",
    method_of_packing: raw.method_of_packing || "",

    // Invoice & Dates
    invoice_no: raw.invoice_no || "",
    loading_in_date: raw.loading_date || "",
    loading_out_date: raw.loading_out_date || "",
    unloading_in_date: raw.unloading_date || "",
    unloading_out_date: raw.unloading_out_date || "",

    // Bill to
    bill_to,

    // Package items
    package_items,

    // Charges
    freight: raw.freight_amt || "",
    mazdoor_charges: "",
    service_tax_cess: "",
    sur_ch: "",
    st_ch: raw.st_charge || "",
    risk_ch: "",
    total_rs: raw.net_payable || raw.freight_amt || "",
    total_p: "",

    // Billed at
    billed_at: "BHILAI",

    // Value
    value: raw.value || "",
  };
  const item = d.package_items[0];
  const freightLabels = [
    "LR",
    "Mazdoor Ch.",
    "Ser. Tax & Cess",
    "Sur. Ch.",
    "St. Ch.",
    "Risk Ch.",
  ];
  const FIXED_ROWS = Math.max(package_items.length, freightLabels.length);
  const dataRowCount = FIXED_ROWS;
  const TOTAL_ROWS_HEIGHT = 150;
  const MIN_ROW_HEIGHT = 8;
  const dataRowHeight = Math.max(
    TOTAL_ROWS_HEIGHT / FIXED_ROWS,
    MIN_ROW_HEIGHT,
  );
  const dataRows = [];
  for (let i = 0; i < dataRowCount; i++) {
    const p = package_items[i] || {};
    const row = [
      { text: p.packages || "", fontSize: 8 },
      { text: p.description || "", fontSize: 8 }, // noWrap = no extra lines = even height
      {
        text: p.actual_weight ? `${Number(p.actual_weight).toFixed(3)} MT` : "",
        fontSize: 8,
        alignment: "center",
      },
      {
        text: p.charged_weight
          ? `${Number(p.charged_weight).toFixed(3)} MT`
          : "",
        fontSize: 8,
        alignment: "center",
      },
      { text: freightLabels[i] || "", fontSize: 8 },
    ];

    if (i === 0) {
      row.push(
        {
          text: "TO BE",
          bold: true,
          fontSize: 9,
          alignment: "center",
          rowSpan: 3,
        },
        {},
      );
    } else if (i === 1 || i === 2) {
      row.push({}, {});
     } else if (i === 3) {
      row.push(
        {
          text: "BILLED",
          bold: true,
          fontSize: 9,
          alignment: "center",
          rowSpan: 2,
        },
        {},
      );
    } else if (i === 4) {
      row.push({}, {});
    } else if (i === 5) {
      row.push(
        { text: "AT BHILAI", bold: true, fontSize: 9, alignment: "center" },
        {},
      );
    } else if (i === 6) {
      row.push(
        { text: "BHILAI", bold: true, fontSize: 9, alignment: "center" },
        {},
      );
    } else {
      row.push({ text: "", fontSize: 8 }, { text: "", fontSize: 8 });
    }
    dataRows.push(row);
  }

  dataRows.push([
    { text: "", fontSize: 8 },
    { text: "", fontSize: 8 },
    { text: "", fontSize: 8 },
    { text: "", fontSize: 8 },
    {
      text: "TOTAL",
      bold: true,
      fontSize: 7.5,
      border: [false, true, false, true],
    },
    { text: d.total_rs || "", fontSize: 7 },
    { text: d.total_p || "", fontSize: 7 },
  ]);
  const additionalInfoBox = {
    table: {
      widths: ["*"],
      body: [
        [
          {
            text: "E-Way Bill No. & Date / Validity :",
            fontSize: 8,
            margin: [1, 0, 2, 0],
            bold: true,
          },
        ],

        [
          {
            text: d.eway_bill_no
              ? `E-Way Bill No : ${d.eway_bill_no}\nDt: ${fmtDate(d.eway_bill_date)}   |   Exp: ${fmtDate(d.eway_validity_date)}`
              : " ",
            fontSize: 8,
            margin: [2, 2, 7, 2],
          },
        ],
        [
          {
            text: "Classification of Goods :",
            fontSize: 8,
            margin: [2, 0, 2, 0],
          },
        ],
        // Classification - value row
        [
          {
            text: d.classification_of_goods || " ",
            fontSize: 8,
            margin: [2, 7, 2, 7],
            minHeight: 50, // ✅ restored
          },
        ],
        // Method of Packing - label row
        [
          {
            text: "Method of Packing:",
            fontSize: 8,
            margin: [2, 0, 2, 0],
          },
        ],
        // Method of Packing - value row (fills remaining space)
        [
          {
            text: d.method_of_packing || " ",
            fontSize: 8,
            margin: [2, 8, 2, 8],
            minHeight: 210, // ✅ calculated to fill remaining height
          },
        ],
        // Invoice No.
        [
          {
            columns: [
              {
                text: "Invoice No.  :",
                fontSize: 8,
                width: 95,
                bold: true,
              },
              {
                text: d.invoice_no || "",
                fontSize: 8,
                width: "*",
                alignment: "left",
              },
            ],
            margin: [2, 6, 2, 6],
          },
        ],
        // Loading In Date
        [
          {
            columns: [
              {
                text: "Loading In Date  :",
                fontSize: 8,
                width: 95,
                bold: true,
              },
              {
                text: fmtDate(d.loading_in_date) || "",
                fontSize: 8,
                width: "*",
                alignment: "left",
              },
            ],
            margin: [2, 6, 2, 6],
          },
        ],
        // Loading Out Date
        [
          {
            columns: [
              {
                text: "Loading Out Date  :",
                fontSize: 8,
                width: 95,
                bold: true,
              },
              {
                text: fmtDate(d.loading_out_date) || "",
                fontSize: 8,
                width: "*",
                alignment: "left",
              },
            ],
            margin: [2, 6, 2, 6],
          },
        ],
        // Unloading In Date
        [
          {
            columns: [
              {
                text: "Unloading In Date  :",
                fontSize: 8,
                width: 95,
                bold: true,
              },
              {
                text: fmtDate(d.unloading_in_date) || "",
                fontSize: 8,
                width: "*",
                alignment: "left",
              },
            ],
            margin: [2, 6, 2, 6],
          },
        ],
        // Unloading Out Date
        [
          {
            columns: [
              {
                text: "Unloading Out Date  :",
                fontSize: 8,
                width: 95,
                bold: true,
              },
              {
                text: fmtDate(d.unloading_out_date) || "",
                fontSize: 8,
                width: "*",
                alignment: "left",
              },
            ],
            margin: [2, 6, 2, 6],
          },
        ],
        // Consignor checkbox
        [
          {
            columns: [
              {
                stack: [
                  {
                    canvas: [
                      {
                        type: "rect",
                        x: 0,
                        y: 0,
                        w: 8,
                        h: 8,
                        lineWidth: 0.75,
                        lineColor: "#000000",
                      },
                      ...(d.bill_to === "consignor"
                        ? [
                            {
                              type: "line",
                              x1: 1.5,
                              y1: 4,
                              x2: 3.5,
                              y2: 6.5,
                              lineWidth: 1,
                              lineColor: "#000000",
                            },
                            {
                              type: "line",
                              x1: 3.5,
                              y1: 6.5,
                              x2: 7,
                              y2: 1.5,
                              lineWidth: 1,
                              lineColor: "#000000",
                            },
                          ]
                        : []),
                    ],
                  },
                ],
                width: 12,
                margin: [0, 1, 3, 0],
              },
              {
                text: "Consignor :",
                fontSize: 8,
                width: "*",
                margin: [0, 0, 0, 0],
              },
            ],
            margin: [2, 5, 2, 5], // ✅ restored original
          },
        ],
        // Consignee checkbox
        [
          {
            columns: [
              {
                stack: [
                  {
                    canvas: [
                      {
                        type: "rect",
                        x: 0,
                        y: 0,
                        w: 8,
                        h: 8,
                        lineWidth: 0.75,
                        lineColor: "#000000",
                      },
                      ...(d.bill_to === "consignee"
                        ? [
                            {
                              type: "line",
                              x1: 1.5,
                              y1: 4,
                              x2: 3.5,
                              y2: 6.5,
                              lineWidth: 1,
                              lineColor: "#000000",
                            },
                            {
                              type: "line",
                              x1: 3.5,
                              y1: 6.5,
                              x2: 7,
                              y2: 1.5,
                              lineWidth: 1,
                              lineColor: "#000000",
                            },
                          ]
                        : []),
                    ],
                  },
                ],
                width: 12,
                margin: [0, 1, 3, 0],
              },
              {
                text: "Consignee :",
                fontSize: 8,
                width: "*",
                margin: [0, 0, 0, 0],
              },
            ],
            margin: [2, 5, 2, 5], // ✅ restored original
          },
        ],
        // Transporter checkbox
        [
          {
            columns: [
              {
                stack: [
                  {
                    canvas: [
                      {
                        type: "rect",
                        x: 0,
                        y: 0,
                        w: 8,
                        h: 8,
                        lineWidth: 0.75,
                        lineColor: "#000000",
                      },
                      ...(d.bill_to === "transporter"
                        ? [
                            {
                              type: "line",
                              x1: 1.5,
                              y1: 4,
                              x2: 3.5,
                              y2: 6.5,
                              lineWidth: 1.2,
                              lineColor: "#000000",
                            },
                            {
                              type: "line",
                              x1: 3.5,
                              y1: 6.5,
                              x2: 7,
                              y2: 1.5,
                              lineWidth: 1.2,
                              lineColor: "#000000",
                            },
                          ]
                        : []),
                    ],
                  },
                ],
                width: 12,
                margin: [0, 1, 3, 0],
              },
              {
                text: [
                  { text: "Transporter : ", fontSize: 8 },
                  { text: d.truck_owner || "", fontSize: 8, bold: true },
                ],
                width: "*",
                margin: [0, 0, 0, 0],
              },
            ],
            margin: [2, 5, 2, 5],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.75,
      vLineWidth: () => 0.75,
      hLineColor: () => "#000000",
      vLineColor: () => "#000000",
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 2,
      paddingBottom: () => 2,
    },
  };

  const docDefinition = {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [11, 11, 11, 11],
    compress: true,
    pageBreakBefore: () => false,
    defaultStyle: {
      fontSize: 8.5,
      font: "Roboto",
      color: "#000",
      lineHeight: 1.2,
    },
    images: { logo1 },

    content: [
      // ═══════════ HEADER (logo pulled tight to the left of the title) ═══════════
      {
        columns: [
          // Left spacer to push logo+title toward center
          { width: "*", text: "" },

          // Logo + Title grouped tightly together
          {
            width: "auto",
            columns: [
              {
                width: 55,
                image: "logo1",
                fit: [50, 45],
                margin: [0, 4, 20, 0],
                alignment: "right",
              },
              {
                width: "auto",
                stack: [
                  {
                    text: "Subject to Durg Jurisdiction",
                    fontSize: 8,
                    alignment: "center",
                  },
                  {
                    text: "PARAS ROADLINES",
                    fontSize: 20,
                    bold: true,
                    characterSpacing: 0.5,
                    alignment: "center",
                    margin: [0, 1, 0, 0],
                  },
                  {
                    text: "FLEET OWNERS & TRANSPORTING CONTRACTORS",
                    fontSize: 8,
                    bold: true,
                    alignment: "center",
                  },
                  {
                    text: "52, R.S. Shukla Market, Power House, Bhilai - 490 011 (Chhattisgarh)",
                    fontSize: 7,
                    alignment: "center",
                  },
                ],
              },
            ],
            columnGap: 0,
          },

          // Right spacer + contact info on the far right
          {
            width: "*",
            stack: [
              {
                columns: [
                  {
                    text: "OFF",
                    fontSize: 8,
                    width: 30,
                    alignment: "left",
                  },
                  {
                    text: ":",
                    fontSize: 8,
                    width: 10,
                    alignment: "left",
                  },
                  {
                    text: "7999552398, 9425234581, 7974125386",
                    fontSize: 8,
                    width: "*",
                    alignment: "left",
                  },
                ],
                margin: [80, 0, 0, 1],
              },
              {
                columns: [
                  {
                    text: "Email",
                    fontSize: 8,
                    width: 30,
                    alignment: "left",
                  },
                  {
                    text: ":",
                    fontSize: 8,
                    width: 10,
                    alignment: "left",
                  },
                  {
                    text: "paras_roadlines@rediffmail.com",
                    fontSize: 8,
                    width: "*",
                    alignment: "left",
                  },
                ],
                margin: [80, 0, 0, 6],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 6],
      },

      // ═══════════ MAIN BODY: left content + right Additional Info sidebar ═══════════
      {
        columns: [
          // ───────────── LEFT (everything except sidebar) ─────────────
          {
            width: "77%",
            stack: [
              // 4-column info row
              {
                columns: [
                  // A: Demurrage + Notice
                  {
                    width: "30%",
                    stack: [
                      {
                        table: {
                          widths: ["*"],
                          body: [
                            [
                              {
                                text: "SCHEDULE OF DEMURRAGE CHARGES",
                                bold: true,
                                fontSize: 9,
                                alignment: "center",
                                margin: [0, 2, 0, 0],
                              },
                            ],
                            [
                              {
                                text: `Demurrage chargeable after ${d.demurrage_days || "_______"} days`,
                                fontSize: 8,
                                margin: [0, 2, 0, 2],
                              },
                            ],
                            [
                              {
                                text: `from today @ Rs. ${d.demurrage_rate || "_______"} per day per Qtl. on weight charged.`,
                                fontSize: 8,
                                margin: [0, 2, 0, 2],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: (i, node) => {
                            if (i === 0) return 0.75; // Top border
                            if (i === 1) return 0.5; // Bottom line under the title
                            if (i === node.table.body.length) return 0.75; // Bottom border
                            return 0; // No other internal lines
                          },
                          vLineWidth: (i, node) => {
                            if (i === 0 || i === node.table.widths.length)
                              return 0.75;
                            return 0;
                          },
                          hLineColor: () => "#000",
                          vLineColor: () => "#000",
                          paddingLeft: () => 3,
                          paddingRight: () => 3,
                          paddingTop: () => 1,
                          paddingBottom: () => 1,
                        },
                        margin: [0, 0, 4, 3],
                      },
                      textBox(
                        [
                          {
                            text: "NOTICE",
                            color: "red",
                            bold: true,
                            fontSize: 9,
                            alignment: "center",
                          },
                          {
                            text: "The Consignment covered by this Lorry Receipt shall be stored at the destination under the control of the Transport Operator and shall be delivered to or to the order of the consignee Bank whose name is mentioned in the Lorry Receipt. It will under no circumstances be delivered to anyone without the written authority from the Consignee Bank or its order, endorsed on the Consignee copy.",
                            color: "red",
                            fontSize: 7.2,
                            alignment: "justify",
                          },
                        ],
                        redBorder,
                        [0, 0, 4, 0],
                      ),
                    ],
                  },
                  // B: Consignee Copy / At Carrier's Risk / Insurance / No.
                  {
                    width: "38%",
                    stack: [
                      {
                        text: "CONSIGNEE COPY",
                        color: "red",
                        bold: true,
                        fontSize: 14,
                        alignment: "center",
                      },
                      {
                        text: "AT CARRIER'S RISK",
                        bold: true,
                        fontSize: 14,
                        alignment: "center",
                        margin: [0, 1, 0, 1],
                      },
                      {
                        text: "INSURANCE",
                        bold: true,
                        fontSize: 14,
                        alignment: "center",
                        margin: [0, 1, 0, 1],
                      },
                      {
                        table: {
                          widths: ["*"],
                          body: [
                            [
                              {
                                text: "The consignor has stated that -",
                                fontSize: 8,
                                margin: [0, 2, 1, 0],
                                bold: true,
                              },
                            ],
                            [
                              {
                                text: "He has insured the consignment",
                                fontSize: 8,
                                margin: [0, 0, 1, 2],
                                bold: true,
                              },
                            ],
                            [
                              {
                                columns: [
                                  {
                                    text: `Company: ${d.insurance_company || ""}`,
                                    fontSize: 8,
                                    width: "*",
                                    bold: true,
                                  },
                                ],
                                margin: [0, 1, 1, 1],
                              },
                            ],
                            [
                              {
                                columns: [
                                  {
                                    text: `Policy No. : ${d.policy_no || "______________"}`,
                                    fontSize: 8,
                                    width: "50%",
                                    bold: true,
                                  },
                                  {
                                    text: `Date: ${fmtDate(d.insurance_date) || "______________"}`,
                                    fontSize: 8,
                                    width: "50%",
                                    bold: true,
                                  },
                                ],
                                margin: [0, 1, 1, 1],
                              },
                            ],
                            [
                              {
                                columns: [
                                  {
                                    text: `Amount: ${d.insurance_amount || "______________"}`,
                                    fontSize: 8,
                                    width: "50%",
                                    bold: true,
                                  },
                                  {
                                    text: `Risk: ${d.insurance_risk || "_________________"}`,
                                    fontSize: 8,
                                    width: "50%",
                                    bold: true,
                                  },
                                ],
                                margin: [0, 1, 1, 3],
                              },
                            ],
                            [
                              {
                                text: `LR No.  ${d.lr_number || "______________"}`,
                                color: "#c2185b",
                                bold: true,
                                fontSize: 10,
                                alignment: "center",
                                margin: [0, 4, 0, 4],
                              },
                            ],
                          ],
                        },
                        layout: makeLayout({
                          hRule: (i, node) =>
                            i === 0 ||
                            i === node.table.body.length ||
                            i === node.table.body.length - 1
                              ? 0.5
                              : 0,
                          vRule: (i, node) =>
                            i === 0 || i === node.table.widths.length ? 0.5 : 0,
                          pad: [2, 2, 2, 1],
                        }),
                        margin: [0, 0, 4, 0],
                      },
                    ],
                  },
                  // D: Caution / Issuing office / Vehicle No.
                  {
                    width: "30%",
                    stack: [
                      {
                        text: "CAUTION",
                        bold: true,
                        fontSize: 10,
                        alignment: "center",
                      },
                      {
                        text: "This consignment will not be detained, diverted, re-routed or re-booked without Consignee Bank's written permission.",
                        fontSize: 8,
                        alignment: "justify",
                        margin: [0, 8, 0, 3],
                      },
                      {
                        stack: [
                          {
                            text: `Will be delivered at the destination: ${d.delivery_office_address || ""}`,
                            fontSize: 8,
                            alignment: "left",
                            margin: [2, 4, 2, 4],
                          },
                          {
                            text: `Address of Delivery Office: ${d.delivery_office_address || ""}`,
                            fontSize: 8,
                            alignment: "left",
                            margin: [2, 4, 2, 4],
                          },
                          {
                            columns: [
                              {
                                text: `State:  ${getStateCode(d.state) || "______________"}`,
                                fontSize: 8,
                                bold: "true",
                                width: "50%",
                                alignment: "left",
                              },
                              {
                                text: `Tel No: ${d.tel_no || "______________"}`,
                                fontSize: 8,
                                bold: "true",
                                width: "50%",
                                alignment: "left",
                              },
                            ],
                            margin: [2, 4, 2, 4],
                          },
                        ],
                        margin: [0, 0, 0, 3],
                      },
                      textBox(
                        [
                          {
                            text: "CONSIGNMENT NOTE",
                            bold: true,
                            fontSize: 12,
                            alignment: "center",
                            margin: [2, 4, 2, 4],
                          },
                          {
                            text: `Date:   ${fmtDate(d.date) || "______________"}`,
                            fontSize: 9,
                            alignment: "center",
                            margin: [3, 2, 2, 2],
                          },
                        ],
                        thinBorder,
                        [0, 0, 0, 4],
                      ),
                    ],
                  },
                ],
                columnGap: 5,
                margin: [0, 0, 0, 3],
              },

              // Consignor / Consignee Bank (left) + From / To boxes (right)
              {
                columns: [
                  {
                    width: "63%",
                    table: {
                      widths: ["*"],
                      body: [
                        [
                          {
                            columns: [
                              {
                                text: "Consignor's Name & Address:",
                                fontSize: 9,
                                width: "auto",
                                noWrap: true,
                                margin: [2, 1, 4, 0],
                                bold: true,
                              },
                              {
                                text: d.consignor_name || "",
                                fontSize: 9,
                                width: "*",
                                margin: [0, 1, 0, 0],
                              },
                            ],
                            margin: [0, 1, 0, 1],
                          },
                        ],
                        [
                          {
                            text: d.consignor_address || "",
                            fontSize: 9,
                            margin: [5, 1, 0, 1],
                          },
                        ],
                        [
                          {
                            columns: [
                              {
                                text: "Consignee Bank's Name & Address:",
                                fontSize: 9,
                                width: "auto",
                                noWrap: true,
                                bold: true,
                                margin: [2, 1, 4, 0],
                              },
                              {
                                text: d.consignee_bank_name || "",
                                fontSize: 9,
                                width: "*",
                                margin: [0, 1, 0, 0],
                              },
                            ],
                            margin: [0, 1, 0, 1],
                          },
                        ],
                        [
                          {
                            text: d.consignee_bank_address || "",
                            fontSize: 9,
                            margin: [5, 1, 0, 1],
                          },
                        ],
                      ],
                    },
                    layout: {
                      hLineWidth: (i, node) => {
                        if (i === 0 || i === node.table.body.length)
                          return 0.75;
                        return 0.5;
                      },
                      vLineWidth: (i, node) =>
                        i === 0 || i === node.table.widths.length ? 0.75 : 0,
                      hLineColor: () => "#000",
                      vLineColor: () => "#000",
                      paddingLeft: () => 0,
                      paddingRight: () => 0,
                      paddingTop: () => 0,
                      paddingBottom: () => 0,
                    },
                  },
                  {
                    width: "35%",
                    stack: [
                      // From row: label outside on left, box on right
                      {
                        columns: [
                          {
                            text: "From :",
                            fontSize: 8.5,
                            width: 28,
                            margin: [0, 6, 4, 0],
                          },
                          {
                            width: "*",
                            ...singleColTable(
                              [
                                {
                                  text: d.from_location || "",
                                  fontSize: 9,
                                  bold: true,
                                  alignment: "center",
                                  margin: [2, 4, 2, 4],
                                },
                              ],
                              thinBorder,
                              [0, 0, 0, 0],
                            ),
                          },
                        ],
                        margin: [0, 0, 0, 13],
                      },
                      // To row: label outside on left, box on right
                      {
                        columns: [
                          {
                            text: "To :",
                            fontSize: 8.5,
                            width: 28, // ⬅ same fixed width
                            margin: [0, 6, 4, 0],
                          },
                          {
                            width: "*",
                            ...singleColTable(
                              [
                                {
                                  text: d.to_location || "",
                                  fontSize: 9,
                                  bold: true,
                                  alignment: "center",
                                  margin: [2, 4, 2, 4],
                                },
                              ],
                              thinBorder,
                              [0, 0, 0, 0],
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
                columnGap: 5,
                margin: [0, 8, 0, 8],
              },

              // Packages table
              {
                table: {
                  widths: ["10%", "38%", "12%", "12%", "12%", "7%", "7%"],
                  heights: [
                    12, // header row 1
                    5, // header row 2
                    ...Array(dataRows.length).fill(dataRowHeight), // one entry per data+total row
                  ],
                  body: [
                    [
                      {
                        text: "Packages",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                        margin: [0, 8, 0, 0],
                        rowSpan: 2,
                      },
                      {
                        text: "Description (said to contain)",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                        margin: [0, 8, 0, 0],
                        rowSpan: 2,
                      },
                      {
                        text: "Weight",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                        margin: [0, 0, 0, 0],
                        colSpan: 2,
                        border: [true, true, true, true],
                      },
                      {},
                      {
                        text: "Rate",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                        margin: [0, 8, 0, 0],
                        rowSpan: 2,
                      },
                      {
                        text: "Amt To Pay/Paid",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                        margin: [0, 0, 0, 0],
                        colSpan: 2,
                        border: [true, true, true, true],
                      },
                      {},
                    ],
                    [
                      {},
                      {},
                      {
                        text: "Actual",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                      },
                      {
                        text: "Charged",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                      },
                      {},
                      {
                        text: "Rs.",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                      },
                      {
                        text: "P.",
                        bold: true,
                        fontSize: 8,
                        alignment: "center",
                      },
                    ],
                    ...dataRows,
                  ],
                },
                layout: makeLayout({
                  hRule: (i, node) =>
                    i === 0 ||
                    i === 1 ||
                    i === 2 ||
                    i === node.table.body.length
                      ? 0.5
                      : 0,
                  vRule: () => 0.5,
                  pad: [2, 1, 1, 1],
                }),
                margin: [0, 3, 0, 0],
              },

              // Value + Signature
              {
                columns: [
                  {
                    text: `Value:  ${d.value}`,
                    fontSize: 8,
                    width: "auto",
                    alignment: "left",
                  },
                  { text: "", width: "*" },
                  {
                    text: "Signature of the Transport Operator : No need to to signature, it is system generated.",
                    fontSize: 8,
                    width: "auto",
                    alignment: "right",
                    margin: [0, 0, 50, 0],
                  },
                ],
                margin: [2, 10, 0, 0],
              },
            ],
          },

          // ───────────── RIGHT sidebar ─────────────
          {
            width: "23%",
            stack: [
              singleColTable(
                [
                  {
                    text: "Address of Issuing Office or\nName and Address of Agent",
                    bold: true,
                    fontSize: 8,
                    alignment: "center",
                    margin: [1, 3, 1, 3],
                  },
                  {
                    text:
                      d.issuing_office_address || "POWER HOUSE BHILAI (C.G.)",
                    bold: true,
                    fontSize: 10,
                    alignment: "center",
                    margin: [0, 10, 0, 10],
                  },
                  {
                    text: `Vehicle No. : ${d.vehicle_no || "____________"}`,
                    fontSize: 8,
                    margin: [6, 10, 1, 6],
                  },
                ],
                thinBorder,
                [0, 0, 0, 6],
              ),
              {
                table: {
                  widths: ["*"],
                  body: [
                    [
                      {
                        text: "Additional Information",
                        italics: true,
                        bold: true,
                        fontSize: 12,
                        margin: [2, 2, 2, 2],
                      },
                    ],
                  ],
                },
                layout: noBorder,
              },
              additionalInfoBox,
            ],
            margin: [3, 0, 0, 0],
          },
        ],
        columnGap: 2,
      },
    ],
  };

  return docDefinition;
};

export const printLorryReceipt = (rowData) => {
  const docDefinition = generateLorryReceiptPDF(rowData);
  pdfMake.createPdf(docDefinition).open();
};

export const downloadLorryReceipt = (rowData) => {
  const docDefinition = generateLorryReceiptPDF(rowData);
  const filename = `LorryReceipt_${(rowData && rowData.lr_number) || "LR"}.pdf`;
  pdfMake.createPdf(docDefinition).download(filename);
};

export const pdfGenerator = (rowData) => {
  printLorryReceipt(rowData);
  return null;
};

export const getLorryReceiptBlobUrl = (rowData) => {
  return new Promise((resolve, reject) => {
    try {
      const docDefinition = generateLorryReceiptPDF(rowData);
      pdfMake.createPdf(docDefinition).getBlob((blob) => {
        resolve(URL.createObjectURL(blob));
      });
    } catch (err) {
      reject(err);
    }
  });
};
